import { useState, useMemo } from 'react';
import type { LandCategory, Color } from '@mtg-deck-tool/shared';
import { parseManaCost, isLand } from '@mtg-deck-tool/shared';
import { useDeckStore } from '../../stores/deck-store.js';
import { useLandProposalStore, type ResolvedProposalEntry } from '../../stores/land-proposal-store.js';
import { LAND_CATALOG } from '../../data/land-catalog.js';
import { SearchResultRow } from '../search/SearchResultRow.js';
import { ManaOverviewPanel } from './ManaOverviewPanel.js';
import { LandDiffPanel } from './LandDiffPanel.js';
import { computeKarstenSources, computeKarstenTargets, expandHybridPipCounts } from '../../lib/land-sim/karsten-sources.js';
import { buildSimLandCardWithCategory } from '../../lib/land-sim/sim-selection.js';
import type { ManaOverviewRow } from '../../lib/land-sim/karsten-types.js';

// ─── Category display order and labels ────────────────────────────────────────

const CATEGORY_ORDER: LandCategory[] = [
  'rainbow',
  'fetch-on',
  'fetch-off',
  'shock',
  'marvel-dual',
  'og-dual',
  'surveil',
  'triome',
  'bond',
  'turbulent',
  'verge',
  'fast',
  'check',
  'bounce',
  'pain',
  'filter',
  'slow',
  'reveal',
  'pathway',
  'horizon',
  'battle',
  'tainted',
  'basic',
];

const CATEGORY_LABELS: Partial<Record<LandCategory, string>> = {
  'rainbow': 'Multicolor',
  'fetch-on': 'Fetch Lands (On-Color)',
  'fetch-off': 'Fetch Lands (Off-Color)',
  'shock': 'Shock Lands',
  'marvel-dual': 'Marvel Dual Lands',
  'og-dual': 'Original Duals',
  'surveil': 'Surveil Lands',
  'check': 'Check Lands',
  'bounce': 'Bounce Lands',
  'fast': 'Fast Lands',
  'slow': 'Slow Lands',
  'reveal': 'Reveal Lands',
  'turbulent': 'Turbulent Lands',
  'verge': 'Verge Lands',
  'pain': 'Pain Lands',
  'filter': 'Filter Lands',
  'pathway': 'Pathway Lands',
  'horizon': 'Horizon Lands',
  'bond': 'Bond Lands',
  'battle': 'Battle Lands',
  'tainted': 'Tainted Lands',
  'triome': 'Triomes',
  'basic': 'Basic Lands',
};

// ─── ProposalCardRow ─────────────────────────────────────────────────────────

/**
 * Renders a card row in the proposal view.
 * For basics (quantity > 1), shows the PROPOSAL quantity instead of the deck quantity.
 * For non-basics, delegates to SearchResultRow which shows deck quantity.
 */
function ProposalCardRow({ entry }: { entry: ResolvedProposalEntry }) {
  if (entry.category === 'basic' && entry.quantity > 1) {
    // Render basic with proposal quantity — don't use SearchResultRow
    // which would show the old deck quantity
    return (
      <div className="flex items-center gap-1.5 px-3 py-1 text-sm">
        <span className="text-[10px] text-green-400 font-bold tabular-nums shrink-0 w-4 text-right">
          {entry.quantity}
        </span>
        <span className="text-gray-200 text-xs font-medium truncate">
          {entry.card.name}
        </span>
        <span className="text-gray-500 text-[10px] truncate">
          Basic Land
        </span>
      </div>
    );
  }

  return (
    <div className={entry.alreadyInDeck ? 'opacity-40' : ''}>
      <SearchResultRow card={entry.card} />
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Displays a pending land proposal in the search results area.
 *
 * Shows a confirmation banner (Accept / Cancel), three analytics panels
 * (Karsten source coverage, mana overview, land diff), and the proposed
 * lands grouped by category in a two-column layout.
 *
 * On Accept, commits to the deck. On Cancel, clears.
 */
export function LandProposalView() {
  const pendingProposal = useLandProposalStore((s) => s.pendingProposal);
  const clearPendingProposal = useLandProposalStore((s) => s.clearPendingProposal);

  const entries = useDeckStore((s) => s.entries);
  const bulkAddCards = useDeckStore((s) => s.bulkAddCards);
  const removeLandEntries = useDeckStore((s) => s.removeLandEntries);
  const deckColorIdentityFromStore = useDeckStore((s) => s.colorIdentity);

  const [isAccepting, setIsAccepting] = useState(false);

  // Compute old mana overview from current deck (before accepting)
  const oldManaOverview = useMemo((): ManaOverviewRow[] => {
    const ci = deckColorIdentityFromStore as Color[];
    if (ci.length === 0) return [];
    const ALL_COLORS: Color[] = ['W', 'U', 'B', 'R', 'G'];

    const nameToCE = new Map<string, { entry: any; cycle: any }>();
    for (const cycle of LAND_CATALOG) {
      for (const entry of cycle.entries) nameToCE.set(entry.name, { entry, cycle });
    }

    const simLands: Array<any> = [];
    for (const e of entries) {
      if (e.isCommander) continue;
      if (!isLand(e.card)) continue;
      const found = nameToCE.get(e.card.name);
      if (found) {
        const sc = buildSimLandCardWithCategory(found.entry, found.cycle);
        for (let i = 0; i < e.quantity; i++) simLands.push(sc);
      } else {
        const oracle = (e.card.oracleText ?? '').toLowerCase();
        const isAny = oracle.includes('mana of any color') || oracle.includes('any combination of colors');
        let pc: Set<Color>;
        if (isAny) { pc = new Set<Color>(); }
        else {
          const c = new Set<Color>();
          for (const m of oracle.matchAll(/\{([wubrgWUBRG])\}/g)) {
            const s = m[1]!.toUpperCase();
            if (ALL_COLORS.includes(s as Color)) c.add(s as Color);
          }
          pc = c.size > 0 ? c : new Set<Color>(['C' as Color]);
        }
        for (let i = 0; i < e.quantity; i++) {
          simLands.push({ name: e.card.name, producedColors: pc, basicLandTypes: new Set(), isLand: true, alwaysUntapped: true, alwaysTapped: false, isFetch: false, isBasic: false, isVerge: false, _category: 'rainbow' });
        }
      }
    }
    if (simLands.length === 0) return [];

    const ls = computeKarstenSources(simLands, ci);
    const rawPips = new Map<string, number>();
    for (const e of entries) {
      if (e.isCommander) continue;
      if (isLand(e.card)) continue;
      if (!e.card.manaCost) continue;
      for (const t of parseManaCost(e.card.manaCost)) {
        const sym = t.slice(1, -1);
        if (ALL_COLORS.includes(sym as Color)) rawPips.set(sym, (rawPips.get(sym) ?? 0) + e.quantity);
      }
    }
    const pipCounts = expandHybridPipCounts(rawPips);
    const totalPips = Array.from(pipCounts.values()).reduce((s, v) => s + v, 0);
    const kt = computeKarstenTargets(ci.length, pipCounts);
    const totalLandCount = simLands.length;

    return ci.map((color) => {
      const pips = pipCounts.get(color) ?? 0;
      const l = ls.get(color) ?? 0;
      const target = kt.targets.get(color) ?? 0;
      const pipPct = totalPips > 0 ? (pips / totalPips) * 100 : 0;
      const prodPct = totalLandCount > 0 ? (l / totalLandCount) * 100 : 0;
      return { color, pipDemandPct: Math.round(pipPct * 10) / 10, productionPct: Math.round(prodPct * 10) / 10, diffPct: Math.round((prodPct - pipPct) * 10) / 10, landSources: l, nonLandSources: 0, target };
    });
  }, [entries, deckColorIdentityFromStore]);

  if (!pendingProposal) return null;

  const {
    resolvedEntries,
    rawProposal,
    mode,
    deckColorIdentity: proposalColorIdentity,
    karstenRows,
    manaOverviewRows,
    landDiff,
    beforeSnapshot,
  } = pendingProposal;

  // Prefer the identity stored at proposal time; fall back to current deck identity
  const colorIdentity = (proposalColorIdentity ?? deckColorIdentityFromStore) as Color[];

  // Build a catalog name set to distinguish catalog-rainbow (Command Tower, etc.)
  // from non-catalog utility lands (Reliquary Tower, MDFCs) that were assigned 'rainbow'
  // as a fallback category in buildProposalFromNames.
  const catalogNameSet = new Set<string>();
  for (const cycle of LAND_CATALOG) {
    for (const entry of cycle.entries) {
      catalogNameSet.add(entry.name);
    }
  }

  // Utility lands: non-catalog entries tagged as 'rainbow' (Reliquary Tower, MDFCs, etc.)
  // Staple lands: catalog 'rainbow' entries (Command Tower, Mana Confluence, etc.) — new or kept
  const utilityEntries = resolvedEntries.filter(
    (e) => e.category === 'rainbow' && !catalogNameSet.has(e.card.name),
  );
  const stapleEntries = resolvedEntries.filter(
    (e) => e.category === 'rainbow' && catalogNameSet.has(e.card.name),
  );
  const dualEntries = resolvedEntries.filter((e) => e.category !== 'rainbow');

  // Total count = everything (new + kept)
  const newEntries = resolvedEntries.filter((e) => !e.alreadyInDeck);
  const keptEntries = resolvedEntries.filter((e) => e.alreadyInDeck);
  const totalNewCount = newEntries.reduce((sum, e) => sum + e.quantity, 0);
  const keptUtilityCount = keptEntries.reduce((sum, e) => sum + e.quantity, 0);
  const totalCount = totalNewCount + keptUtilityCount;

  // Group duals by category in display order
  const grouped = new Map<LandCategory, ResolvedProposalEntry[]>();
  for (const entry of dualEntries) {
    const existing = grouped.get(entry.category) ?? [];
    grouped.set(entry.category, [...existing, entry]);
  }
  const orderedCategories = CATEGORY_ORDER.filter((cat) => cat !== 'rainbow' && grouped.has(cat));

  function handleAccept() {
    setIsAccepting(true);
    try {
      if (mode === 'generate') {
        removeLandEntries();
        // After wiping all lands, add everything: new + kept utility (slate is clean)
        const toAdd = resolvedEntries.map((e) => ({ card: e.card, quantity: e.quantity }));
        bulkAddCards(toAdd);
      } else {
        // Complete mode: only add the entries not already in the deck
        const toAdd = newEntries.map((e) => ({ card: e.card, quantity: e.quantity }));
        bulkAddCards(toAdd);
      }
      clearPendingProposal();
    } finally {
      setIsAccepting(false);
    }
  }

  function handleCancel() {
    clearPendingProposal();
  }

  void rawProposal;

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Confirmation banner */}
      <div className="sticky top-0 z-10 bg-gray-900 border-b border-orange-700/40 px-4 py-3 flex items-center justify-between gap-3 shadow-lg">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-orange-300">
            Land base ready — {totalCount} land{totalCount !== 1 ? 's' : ''} total
            {keptUtilityCount > 0 && (
              <span className="text-gray-500 font-normal">
                {' '}({totalNewCount} new + {keptUtilityCount} kept)
              </span>
            )}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Accept to add to your deck, or cancel to discard.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleCancel}
            className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={isAccepting}
            className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
              isAccepting
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-green-700 hover:bg-green-600 text-white'
            }`}
          >
            {isAccepting ? 'Adding...' : 'Accept Land Base'}
          </button>
        </div>
      </div>

      {/* Mana Overview Panel */}
      {manaOverviewRows && manaOverviewRows.length > 0 && (
        <ManaOverviewPanel rows={manaOverviewRows} {...(oldManaOverview.length > 0 ? { oldRows: oldManaOverview } : {})} />
      )}

      {/* Land Diff Panel — only when deck had lands before generation */}
      {landDiff && beforeSnapshot && beforeSnapshot.size > 0 && (
        <LandDiffPanel diff={landDiff} />
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-2 gap-x-0">

        {/* ── Left column: Multicolor, Utility, then first half of dual categories ── */}
        <div className="border-r border-gray-800/50">
          {/* Multicolor (staple) lands at the top — new + kept from catalog */}
          {stapleEntries.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
                Multicolor
                <span className="ml-1.5 text-gray-600 font-normal normal-case">
                  ({stapleEntries.reduce((s, e) => s + e.quantity, 0)})
                </span>
              </div>
              {stapleEntries.map((entry) => (
                <div key={entry.card.oracleId} className={entry.alreadyInDeck ? 'opacity-60' : ''}>
                  <SearchResultRow card={entry.card} />
                </div>
              ))}
            </div>
          )}

          {/* Utility lands (non-catalog, e.g. Reliquary Tower, MDFCs) */}
          {utilityEntries.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
                Utility Lands
                <span className="ml-1.5 text-gray-600 font-normal normal-case">
                  ({utilityEntries.reduce((s, e) => s + e.quantity, 0)}{utilityEntries.every((e) => e.alreadyInDeck) ? ' kept' : ''})
                </span>
              </div>
              {utilityEntries.map((entry) => (
                <div key={entry.card.oracleId} className="opacity-60">
                  <SearchResultRow card={entry.card} />
                </div>
              ))}
            </div>
          )}

          {/* First half of dual categories */}
          {orderedCategories.slice(0, Math.ceil(orderedCategories.length / 2)).map((category) => {
            const catEntries = grouped.get(category)!;
            const countDisplay = catEntries.reduce((sum, e) => sum + e.quantity, 0);
            const label = CATEGORY_LABELS[category] ?? category;

            return (
              <div key={category}>
                <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
                  {label}
                  {countDisplay > 0 && (
                    <span className="ml-1.5 text-gray-600 font-normal normal-case">
                      ({countDisplay})
                    </span>
                  )}
                </div>
                {catEntries.map((entry) => (
                  <ProposalCardRow key={`${entry.card.oracleId}-${entry.category}`} entry={entry} />
                ))}
              </div>
            );
          })}
        </div>

        {/* ── Right column: second half of dual categories ── */}
        <div>
          {orderedCategories.slice(Math.ceil(orderedCategories.length / 2)).map((category) => {
            const catEntries = grouped.get(category)!;
            const countDisplay = catEntries.reduce((sum, e) => sum + e.quantity, 0);
            const label = CATEGORY_LABELS[category] ?? category;

            return (
              <div key={category}>
                <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-900/60 border-b border-gray-800/50">
                  {label}
                  {countDisplay > 0 && (
                    <span className="ml-1.5 text-gray-600 font-normal normal-case">
                      ({countDisplay})
                    </span>
                  )}
                </div>
                {catEntries.map((entry) => (
                  <ProposalCardRow key={`${entry.card.oracleId}-${entry.category}`} entry={entry} />
                ))}
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}
